import { usePrevious } from '@sequencehq/utils'
import { useEffect, useRef } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import {
  awaitingBulkActionAtom,
  invoiceIdAtom
} from '../atoms/invoiceEditing.atom'
import { useUnreviewedBulkActionsForInvoice } from 'modules/BulkActions'
import { useNotifications } from '@sequencehq/core-components'
import { useInvalidateInvoice } from './useInvalidateInvoice'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useMutation } from '@sequencehq/api'
import { dequal } from 'dequal'

export const useConnectBulkActions = () => {
  const id = useAtomValue(invoiceIdAtom)

  const [awaitingBulkAction, setAwaitingBulkAction] = useAtom(
    awaitingBulkActionAtom
  )

  const { bulkActions } = useUnreviewedBulkActionsForInvoice(
    id ?? '',
    awaitingBulkAction
  )

  const { displayPromiseNotification } = useNotifications()

  const invalidateInvoice = useInvalidateInvoice()

  const reviewBulkAction = useMutation(
    dashboard20240730Client.postReviewBulkAction
  )

  const bulkActionNotificationPromiseHandler = useRef<
    | undefined
    | { resolve: (value: unknown) => void; reject: (reason?: any) => void }
  >(undefined)

  const prevBulkActions = usePrevious(bulkActions)

  useEffect(() => {
    // Only continue if bulk actions have changed
    if (dequal(prevBulkActions, bulkActions)) {
      return
    }

    if (!awaitingBulkAction) {
      bulkActionNotificationPromiseHandler.current = undefined
      return
    }

    // We're awaiting a bulk action and haven't yet shown a notification
    if (!bulkActionNotificationPromiseHandler.current) {
      const promise = new Promise((resolve, reject) => {
        bulkActionNotificationPromiseHandler.current = { resolve, reject }
      })
      displayPromiseNotification(promise, {
        loading: 'Converting to draft',
        success: 'Converted to draft',
        error: 'Failed to convert to draft'
      })
    }

    // We currently only care about convert to draft bulk actions
    const convertToDraftBulkActions = bulkActions.filter(
      bulkAction => bulkAction.type === 'CONVERT_INVOICES_TO_DRAFT'
    )

    // Waiting for bulk actions
    if (convertToDraftBulkActions.length === 0) {
      return
    }

    // Still processing
    if (
      convertToDraftBulkActions.some(
        bulkAction => bulkAction.status === 'PROCESSING'
      )
    ) {
      return
    }

    // Processing finished
    if (
      convertToDraftBulkActions.every(
        bulkAction => bulkAction.status === 'COMPLETED'
      )
    ) {
      bulkActionNotificationPromiseHandler.current?.resolve(true)
      invalidateInvoice()
      for (const bulkAction of convertToDraftBulkActions) {
        reviewBulkAction.mutate({ id: bulkAction.id })
      }
    } else {
      bulkActionNotificationPromiseHandler.current?.reject(
        new Error('Failed to convert to draft')
      )
    }

    setAwaitingBulkAction(false)
  }, [
    bulkActions,
    prevBulkActions,
    awaitingBulkAction,
    setAwaitingBulkAction,
    displayPromiseNotification,
    invalidateInvoice,
    reviewBulkAction
  ])
}
